import uiI18n from "../i18n";
import { useTranslation as useUiTranslation } from "react-i18next";
import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase';
import { engagementApi } from '../Services/engagementService';
import { toast } from 'react-toastify';
const Context = createContext(null);
export function EngagementProvider({ children }) {
  useUiTranslation();
  const [user, setUser] = useState(null);
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const locks = useRef(new Set());
  const version = useRef(0);
  const session = useRef(0);
  const refresh = useCallback(async () => {
    const uid = auth.currentUser?.uid;
    if (!uid || locks.current.size) return;
    const request = ++version.current;
    setLoading(true);
    try {
      const data = await engagementApi.favorites();
      if(request === version.current && auth.currentUser?.uid === uid) { setFavorites(data); setError(''); }
    } catch(e) { if(request === version.current) setError(e.message); }
    finally { if(request === version.current) setLoading(false); }
  }, []);
  useEffect(() => onAuthStateChanged(auth, next => {
    session.current++; version.current++; locks.current.clear(); setFavorites([]); setError(''); setUser(next); setLoading(false);
    if(next) refresh();
  }), [refresh]);
  useEffect(() => {
    const visible = () => { if(document.visibilityState === 'visible') refresh(); };
    window.addEventListener('focus', refresh); document.addEventListener('visibilitychange', visible);
    const timer = setInterval(visible, 60000);
    return () => { clearInterval(timer); window.removeEventListener('focus',refresh); document.removeEventListener('visibilitychange',visible); };
  }, [refresh]);
  async function toggle(kind, listing) {
    const generation = session.current;
    if (!auth.currentUser) { toast.info(uiI18n.t("ui.sign_in_to_use_favorites")); return; }
    if(error) { toast.error(uiI18n.t("ui.refresh_your_favorites_before_trying_again")); refresh(); return; }
    const id = listing.id || listing.ID; const key = `${kind}:${id}`; const uid = auth.currentUser.uid;
    if(!id || loading || locks.current.has(key)) return;
    const old = favorites.find(item => item.kind === kind && item.id === id);
    locks.current.add(key); version.current++;
    setFavorites(items => old ? items.filter(item => `${item.kind}:${item.id}` !== key) : [...items, {...listing,id,kind}]);
    try { await engagementApi.favorite(kind,id,!old); }
    catch(e) {
      if(auth.currentUser?.uid === uid && generation === session.current) {
        setFavorites(items => old ? [...items.filter(item=>`${item.kind}:${item.id}`!==key),old] : items.filter(item=>`${item.kind}:${item.id}`!==key));
        toast.error(e.message);
      }
    } finally { if(generation === session.current) locks.current.delete(key); }
  }
  return <Context.Provider value={{user,favorites,loading,error,refresh,toggle}}>{children}</Context.Provider>;
}
export const useEngagement = () => useContext(Context);
export function useFavorite(kind, listing) {
  useUiTranslation();
  const state = useEngagement();
  const id = listing?.id || listing?.ID;
  return { isFavorite: state.favorites.some(item=>item.kind===kind && item.id===id),
    toggleFavorite: () => state.toggle(kind,listing), favoriteLoading: state.loading };
}
